import React, { useState, useEffect } from 'react'
import useSiteData from '../../useSiteData'
import { formatDate, truncateRichText } from '../../util/transforms'
import { getBlogPosts, getBlogPostsInCategory } from '../../api'
import { MEDIUM } from '../util/breakpoints'
import * as queryString from 'query-string'
import { HeaderThemeContext, headerThemes } from '../../context'
import Loader from '../molecules/Loader'
import Filter from '../organisms/Filter'
import Pagination from '../organisms/Pagination'
import BlogImageText from '../organisms/BlogImageText'
import useMediaQuery from '../util/useMediaQuery'
import App from "../App";
import Head from '../Head'
import IconText from "../molecules/IconText"


const getTotalBlogPages = (blogCount, latestPostsCount, blogsPerPage) => {
  return Math.ceil((blogCount - latestPostsCount) / blogsPerPage) + 1
}

const transformCategoriesWithComma = categories => categories.map(({ name }) => name).join(', ')

const getCategoryId = (categoryName, categories) => categories.find(category => category.name === categoryName).id

const getQueryFilter = () => typeof window !== 'undefined' && queryString.parse(window.location.search).category

const scrollToTop = () => {
  if (typeof window !== 'undefined') {
    window.scrollTo(0, 0)
  }
}

export default ({ pageContext }) => {

  const {
    navigationTitle,
    slug,
    slugs,
    categories,
    allPostsFilter = 'All posts',
    blogs,
    blogsPerPage = 10,
    pageId,
    totalPages,
    latestPostsCount,
    pagePath,
    noHrefLangInHead,
    noIndexFollow
  } = pageContext
  
  const [{
    filteredBlogs,
    appliedFilter,
    activePage,
    totalPagesFiltered = totalPages
  }, setBlogs] = useState({ filteredBlogs: [] })

  const blogSlug = useSiteData().blogSlug
  const { translations } = useSiteData().pageSettings
  const queryFilter = getQueryFilter()
  const isDesktop = useMediaQuery({ minWidth: MEDIUM })
  const categoryId = queryFilter && getCategoryId(queryFilter, categories)
  const pageTitle = navigationTitle + (pageId > 1 ? ' - ' + translations.page + ' ' + pageId : '') + ' - Lapoint Surf camps'

  const [ havePosts, setHavePosts] = useState( true )
  const [ loading, setLoading ] = useState( false )
  const [ blogsToRender, setBlogsToRender ] = useState( [] )

  const getFilteredBlogsToRender = () => filteredBlogs.slice( (pageId - 1) * blogsPerPage, pageId * blogsPerPage )

  // Component Did Mount. Set blogs to render based on inital data. Will be blogs if coming into /blog or empty if starting on a category
  useEffect(() => {
    setBlogsToRender( !queryFilter ? blogs : (activePage === pageId ? filteredBlogs : []) )
  },[])

  // filteredBlogs have changed. We are done fetching data. Set loading to false and blogs to render
  useEffect(() => {
    scrollToTop()
    setLoading( false ) // done loading    
    setBlogsToRender( !queryFilter ? blogs : (activePage === pageId ? getFilteredBlogsToRender() : []) )
  }, [filteredBlogs])

  // Dynamically fetch blog posts whenever the categoryId changes. 
  // Or update which blogs to render if the pageID changes and we have a category
  // During fetch set loading to true and blogs to render to empty array so the loader is the only thing visible
  useEffect(() => {
    if (categoryId && (queryFilter !== appliedFilter || filteredBlogs.length === 0)) {
      // if the category has changed get all posts in that category
      // and since we set filteredBlogs the effect above will trigger      
      setLoading( true ) // show loader
      setBlogsToRender( [] ) // hide currently displayed posts

      getBlogPosts(undefined, 0, categoryId).then(blogs => {
        setBlogs({
          filteredBlogs: blogs,
          appliedFilter: queryFilter,
          activePage: pageId,
          totalPagesFiltered: getTotalBlogPages(blogs.length, latestPostsCount, blogsPerPage)
        })
      })

    } else if ( categoryId && activePage !== pageId  ) {

      setBlogsToRender( !queryFilter ? blogs : (activePage === pageId ? getFilteredBlogsToRender() : []) )

    } else { // no category anymore i.e /blog

      setBlogsToRender( !queryFilter ? blogs : (activePage === pageId ? getFilteredBlogsToRender() : []) )

    }    
  }, [categoryId, pageId])
  // TODO: Currently everytime you use the pagination with a category we fetch all posts in that category. This
  // is because pagination creates a link to this page. And since it is reloaded the component mounts resetting the state of setBlogs
  // Either fix by creating a new version of getBlogPosts that also returns number of entries for the category and then we can safely
  // fetch 3 entries at a time and still now how many entries
  // Or make pagination use a callback and we can simply change pages within a category without refetching all

  return (
    <HeaderThemeContext.Provider value={{ theme: headerThemes.DARK }}>
      <App>
        { noHrefLangInHead &&
          <Head title={ pageTitle } pagePath={ pagePath } noIndexFollow={ noIndexFollow }/>
        }
        { !noHrefLangInHead &&
          <Head title={ pageTitle } slugs={ slugs } pagePath={ pagePath } noIndexFollow={ noIndexFollow }/>
        }
        <main>
          <section
            className={ 'u-center-text' +
            (isDesktop ? ' u-container u-section-spacing  u-section-spacing--narrow' : '') }
          >
            <Filter
              slug={ '/' + blogSlug }
              categories={ categories.map(category => category.name) }
              allSelectedText={ allPostsFilter }
              selectedFilter={ queryFilter }
            />
          </section>
          <section className="u-container">
            { loading &&
              <Loader classes="u-section-spacing"/>
            }
            { ( blogsToRender.length === 0 && !loading ) &&
              <div className="u-section-spacing  u-center-text">
                <IconText layout='' icon='Wave' label={ (translations.noBlogPostsFound ? translations.noBlogPostsFound : 'No posts found') } largeTitle='' title=''/>
              </div>
            }
            { blogsToRender.map((blog, index) =>
              <BlogImageText
                key={ index }
                title={ blog.title }
                text={ truncateRichText(blog.content, 218) }
                subtitle={ formatDate(blog.updatedAt) }
                image={ blog.image }
                categoryText={ blog.categories ? transformCategoriesWithComma(blog.categories) : '' }
                readMoreText={ (translations.readMore ? translations.readMore : 'Read more') }
                url={ '/' + slug + '/' + blog.slug }
              />
            ) }
          </section>
          <section className={ 'u-section-spacing' + (isDesktop ? ' u-container' : '') }>
            { blogsToRender.length > 0 &&
              <Pagination
                activePage={ pageId }
                slug={ `${ blogSlug }/page` }
                homeSlug={ '/' + slug }
                queryParams={ typeof window !== 'undefined' && window.location.search }
                pageCount={ queryFilter ? totalPagesFiltered : totalPages }
              />
            }
          </section>
        </main>
      </App>
    </HeaderThemeContext.Provider>
  )
}
